"use client";

// [RUDDERSTACK-OLD disabled 2026-07-20] widget_viewed → now central widget_impression
// (TrackedWidget in NewPage); see webapp/docs/analytics
// import { FireWidgetViewedEvent } from "@/customAnalytics/trackingEvents";
import { cn } from "@/lib/utils";
import Image from "next/image";
import { useMemo, useRef } from "react";

interface MetaRow {
  id?: string | number;
  meta_desktop_image?: string;
  meta_name?: string;
  meta_designation?: string;
  meta_logo?: string;
  meta_logo_alt?: string;
  meta_priority?: number;
  meta_status?: boolean | number;
}

interface SectionData {
  title?: string;
  title_text_color?: string;
  title_1?: string;
  title_1_color?: string;
  description?: string;
  description_text_color?: string;
  description_background_color?: string;
  metadata?: MetaRow[];
  status?: boolean | number;
}

function isRowActive(row: MetaRow) {
  if (row.meta_status === false || row.meta_status === 0) return false;
  return true;
}

const Faculty3: React.FC<{ data: { data: SectionData } }> = ({ data }) => {
  const { data: section } = data;
  const ref = useRef<HTMLElement>(null);

  const {
    title = "",
    title_text_color = "#000000",
    title_1 = "",
    title_1_color = "#E36E26",
    description = "",
    description_text_color = "#000000",
    description_background_color = "#FFFFFF",
    metadata = [],
  } = section || {};

  const rows = useMemo(
    () =>
      [...(metadata || [])]
        .filter(isRowActive)
        .sort(
          (a, b) => (a.meta_priority ?? 0) - (b.meta_priority ?? 0),
        ),
    [metadata],
  );

  // [RUDDERSTACK-OLD disabled 2026-07-20] impression now central; see webapp/docs/analytics
  // useEffect(() => {
  //   if (ref.current) {
  //     FireWidgetViewedEvent(ref, "faculty_section");
  //   }
  // }, []);

  if (!section || section.status === false || section.status === 0) {
    return null;
  }

  return (
    <section
      ref={ref}
      className={cn(
        "[&_*]:![font-family:Poppins,sans-serif]",
        "w-full py-10 min-[768px]:py-14",
      )}
      style={{
        backgroundColor: description_background_color,
        fontFamily: "Poppins, sans-serif",
      }}
    >
      <div className="mx-auto max-w-[1200px] px-4 min-[768px]:px-6">
        <header className="mb-8 text-center min-[768px]:mb-10">
          <h2 className="text-[32px] font-semibold leading-tight tracking-tight">
            <span style={{ color: title_text_color }}>{title}</span>
            {title_1 ? (
              <span style={{ color: title_1_color }}> {title_1}</span>
            ) : null}
          </h2>
          {description ? (
            <p
              className="mt-3 text-[18px] font-normal leading-normal"
              style={{ color: description_text_color }}
            >
              {description}
            </p>
          ) : null}
        </header>

        {/* Mobile: vertical stack. md+: horizontal strip (3 cards + a peek of the 4th) with scroll */}
        <div className="mx-auto w-full min-[768px]:max-w-[calc(276px*3+38px*3+60px)]">
          <div
            className={cn(
              "scrollbar-hide flex flex-col gap-[38px]",
              "min-[768px]:flex-row min-[768px]:items-stretch min-[768px]:justify-start min-[768px]:overflow-x-auto min-[768px]:pb-2",
              "min-[768px]:snap-x min-[768px]:snap-mandatory min-[768px]:scroll-pl-0",
            )}
          >
            {rows.map((row) => (
              <article
                key={String(row.id ?? row.meta_name)}
                className={cn(
                  "flex w-full shrink-0 flex-col overflow-hidden bg-white",
                  "shadow-[0px_4px_17.4px_0px_rgba(0,0,0,0.18)]",
                  "min-[768px]:w-[276px] min-[768px]:min-w-[276px] min-[768px]:snap-start",
                )}
              >
                <div className="relative aspect-[276/156] w-full shrink-0 overflow-hidden bg-neutral-100 min-[768px]:h-[156px] min-[768px]:w-[276px] min-[768px]:aspect-auto">
                  {row.meta_desktop_image ? (
                    <Image
                      src={row.meta_desktop_image}
                      alt={row.meta_name || "Faculty"}
                      width={276}
                      height={156}
                      className="h-full w-full object-cover min-[768px]:h-[156px] min-[768px]:w-[276px]"
                      sizes="(max-width: 767px) 100vw, 276px"
                    />
                  ) : (
                    <div className="h-full min-h-[156px] w-full bg-neutral-200 min-[768px]:min-h-0" aria-hidden />
                  )}
                </div>
                <div className="flex flex-col items-center gap-2 px-3 pb-4 pt-4 text-center">
                  <h3 className="text-[16px] font-bold leading-snug text-black">
                    {row.meta_name}
                  </h3>
                  <p className="text-[14px] font-normal leading-snug text-neutral-800">
                    {row.meta_designation}
                  </p>
                  <div className="mt-2 flex h-[60px] w-[113.99px] shrink-0 items-center justify-center">
                    {row.meta_logo ? (
                      <Image
                        src={row.meta_logo}
                        alt={row.meta_logo_alt || "Logo"}
                        width={114}
                        height={60}
                        className="max-h-[60px] max-w-[113.99px] object-contain"
                      />
                    ) : null}
                  </div>
                </div>
              </article>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default Faculty3;
